import type { FiltrosAvance } from '@adelante/shared';
import { type ReactNode, createContext, useContext, useEffect, useState } from 'react';

/**
 * Estado global de filtros compartido entre todas las vistas BI.
 *
 * El top-bar del LayoutPrincipal lo escribe, las páginas lo leen para
 * construir sus query params. Si una página necesita filtros adicionales
 * (ej. itemCategoryCode para Costos), los maneja localmente.
 *
 * **Persistencia**: estado serializado en localStorage para que cuando el
 * user navega entre vistas (ej. Costos → Avance → vuelvo a Costos) los
 * filtros se mantengan. También sobrevive a reloads del browser.
 *
 * Campos extra sobre FiltrosAvance:
 * - filtroObra: texto LIKE wildcard sobre works_no. Útil para filtrar
 *   "VB-1" → todas las obras que empiezan con VB-1 (VB-1.01, VB-1.02, etc.).
 */

const STORAGE_KEY = 'adelante-bi-obras:filtros-globales:v1';

export type FiltrosGlobales = FiltrosAvance & {
  /** Búsqueda libre por código de obra (LIKE %texto%). Vacío = sin filtro. */
  filtroObra?: string;
};

interface FiltrosContextValue {
  filtros: FiltrosGlobales;
  setFiltros: (f: FiltrosGlobales) => void;
  actualizar: (patch: Partial<FiltrosGlobales>) => void;
  limpiar: () => void;
}

const FiltrosContext = createContext<FiltrosContextValue | null>(null);

const FILTROS_INICIALES: FiltrosGlobales = {};

function cargarDeStorage(): FiltrosGlobales {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return FILTROS_INICIALES;
    const parsed = JSON.parse(raw);
    // Defensa: validar que sea objeto
    if (parsed && typeof parsed === 'object') return parsed;
  } catch {
    // ignorar localStorage no disponible o JSON inválido
  }
  return FILTROS_INICIALES;
}

function guardarEnStorage(f: FiltrosGlobales): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(f));
  } catch {
    // localStorage lleno o no disponible — silencioso
  }
}

export function FiltrosGlobalesProvider({ children }: { children: ReactNode }) {
  const [filtros, setFiltros] = useState<FiltrosGlobales>(cargarDeStorage);

  // Persistir cada cambio
  useEffect(() => {
    guardarEnStorage(filtros);
  }, [filtros]);

  const actualizar = (patch: Partial<FiltrosGlobales>) => {
    setFiltros((prev) => ({ ...prev, ...patch }));
  };

  const limpiar = () => {
    setFiltros(FILTROS_INICIALES);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
  };

  return (
    <FiltrosContext.Provider value={{ filtros, setFiltros, actualizar, limpiar }}>
      {children}
    </FiltrosContext.Provider>
  );
}

export function useFiltrosGlobales() {
  const ctx = useContext(FiltrosContext);
  if (!ctx) {
    throw new Error('useFiltrosGlobales debe usarse dentro de <FiltrosGlobalesProvider />');
  }
  return ctx;
}

/**
 * Convierte filtros globales a query string para fetch.
 */
export function filtrosToQueryString(f: FiltrosGlobales): string {
  const params = new URLSearchParams();
  if (f.worksNos?.length) params.set('worksNos', f.worksNos.join(','));
  if (f.areasCosteo?.length) params.set('areasCosteo', f.areasCosteo.join(','));
  if (f.categoriasTarea?.length) params.set('categoriasTarea', f.categoriasTarea.join(','));
  // Selector jerárquico de periodos (reemplaza el rango anioDesde/Hasta + fecha).
  if (f.aniosSel?.length) params.set('aniosSel', f.aniosSel.join(','));
  if (f.mesesSel?.length) params.set('mesesSel', f.mesesSel.join(','));
  if (f.diasSel?.length) params.set('diasSel', f.diasSel.join(','));
  if (f.estadosSel?.length) params.set('estadosSel', f.estadosSel.join(','));
  if (f.origen) params.set('origen', f.origen);
  if (f.pctAvanceMin !== undefined) params.set('pctAvanceMin', String(f.pctAvanceMin));
  if (f.pctAvanceMax !== undefined) params.set('pctAvanceMax', String(f.pctAvanceMax));
  if (f.filtroObra && f.filtroObra.trim().length > 0) {
    params.set('filtroObra', f.filtroObra.trim());
  }
  if (f.filtroProyecto && f.filtroProyecto.trim().length > 0) {
    params.set('filtroProyecto', f.filtroProyecto.trim());
  }
  if (f.filtroBloque && f.filtroBloque.trim().length > 0) {
    params.set('filtroBloque', f.filtroBloque.trim());
  }
  if (f.filtroModelo && f.filtroModelo.trim().length > 0) {
    params.set('filtroModelo', f.filtroModelo.trim());
  }
  if (f.incluirSSCC === true) {
    params.set('incluirSSCC', 'true');
  }
  if (f.excluirIndirectos === true) {
    params.set('excluirIndirectos', 'true');
  }
  return params.toString();
}
